import { ReactNode } from 'react';
import { pipe } from 'fp-ts/function';
import { TextKey } from '@mews-commander/enums';
import { LocalizedText } from '@mews/framework';
import { LocalizedLabel, ModalBodyWrapper, ModalWrapper, withAsyncContainer } from '@mews-commander/ui';
import { UnicodeEntity } from '@mews/enums';
import { Optional, createUUID } from '@mews/std';
import { WithAccountId, WithBillId } from '@mews-commander/types';
import { Card, Container, Grid, GridItem, Icon, IconName, Stack, Title } from '@optimus-web/core';
import ModalHeader from '../../../../../../legacy/React/Components/Common/ModalHeader';
import { useCloseButton } from '../../../../../../core/windowManager/hooks';
import {
    useInvalidatePaymentsTableRecordsQueries,
    useInvalidatePreauthorizationTableRecordQuery,
} from '../../../../queries';
import ChargePreauthorizationForm from './ChargePreauthorizationForm';
import { getFormIntialValues, hasMultipleGateways } from './utils';
import { useGetChargeablePreauthorizations } from './queries';

interface PaymentChargePreauthorizationProps extends WithBillId, WithAccountId {
    preauthorizationId: string;
}

const PaymentChargePreauthorizationContent = pipe(
    ({ children }: { children: ReactNode }) => (
        <Container padding="200">
            <Stack vertical spacing="200">
                {children}
            </Stack>
        </Container>
    ),
    withAsyncContainer
);

const ChargePreauthorizationTitle = ({
    gatewayType,
    supportsMultipleGateways,
}: {
    gatewayType: Optional<string>;
    supportsMultipleGateways: boolean;
}) => (
    <Title level={3}>
        {gatewayType && <LocalizedText textKey={TextKey.ChargeOnline} />}
        {gatewayType && supportsMultipleGateways && `${UnicodeEntity.NoBreakSpace}(${gatewayType})`}
        {!gatewayType && <LocalizedText textKey={TextKey.ChargedUsingTerminal} />}
    </Title>
);

const ChargePreauthorizationProvider = () => (
    <Container padding={['100', '0']}>
        <Stack itemAlignment="center">
            <LocalizedLabel textKey={TextKey.Provider} as="div" />
            <Container padding={['0', '100']}>
                <Icon value={IconName.MewsLogo} size="large" />
            </Container>
        </Stack>
    </Container>
);

export const PaymentChargePreauthorization = ({
    accountId,
    preauthorizationId,
    billId,
}: PaymentChargePreauthorizationProps) => {
    const {
        data: { preauthorizationUsages } = { preauthorizationUsages: [] },
        isFetching: isChargeablePreauthorizationsDataFetching,
        isError: hasChargeablePreauthorizationsDataFailed,
    } = useGetChargeablePreauthorizations({ customerId: accountId, preauthorizationId, billId });

    const invalidatePaymentsTableRecordsQueries = useInvalidatePaymentsTableRecordsQueries();
    const invalidatePreauthorizationTableRecordQuery = useInvalidatePreauthorizationTableRecordQuery();

    const closeButton = useCloseButton();

    const invalidateQuery = () => {
        invalidatePreauthorizationTableRecordQuery({ accountId });
        invalidatePaymentsTableRecordsQueries({ accountId });
    };

    return (
        <ModalWrapper>
            <ModalHeader
                title={<LocalizedText textKey={TextKey.PreauthorizationCharge} />}
                primaryAction={closeButton}
            />
            <ModalBodyWrapper>
                <PaymentChargePreauthorizationContent
                    hasFailed={hasChargeablePreauthorizationsDataFailed}
                    isFetching={isChargeablePreauthorizationsDataFetching}
                >
                    <Grid
                        justifyItems={preauthorizationUsages.length === 1 ? 'center' : 'stretch'}
                        templateColumns={preauthorizationUsages.length === 1 ? '1fr' : 'repeat(2, 1fr)'}
                    >
                        {preauthorizationUsages.map((usage) => {
                            const scope = createUUID();
                            return (
                                <GridItem minWidth="50%" key={scope}>
                                    <Card padding="200">
                                        <Stack vertical spacing="100">
                                            <ChargePreauthorizationTitle
                                                gatewayType={usage.gatewayType}
                                                supportsMultipleGateways={hasMultipleGateways(preauthorizationUsages)}
                                            />
                                            {usage.gatewayType && <ChargePreauthorizationProvider />}
                                            <Container padding={['100', '0']}>
                                                <ChargePreauthorizationForm
                                                    hasFailed={hasChargeablePreauthorizationsDataFailed}
                                                    isFetching={isChargeablePreauthorizationsDataFetching}
                                                    form={`chargePreauthorizationForm-${scope}`}
                                                    chargeablePreauthorizations={usage.chargeablePreauthorizations}
                                                    initialValues={getFormIntialValues(
                                                        usage.chargeablePreauthorizations,
                                                        preauthorizationId
                                                    )}
                                                    accountId={accountId}
                                                    billId={billId}
                                                    isGateway={!!usage.gatewayType}
                                                    invalidateQuery={invalidateQuery}
                                                />
                                            </Container>
                                        </Stack>
                                    </Card>
                                </GridItem>
                            );
                        })}
                    </Grid>
                </PaymentChargePreauthorizationContent>
            </ModalBodyWrapper>
        </ModalWrapper>
    );
};
